<template>
  <div data-testid="import-form-step" class="flex flex-col gap-y-4 py-2 form-step">
    <v-autocomplete
      v-model="companyValue"
      data-testid="import-form-empresa-select"
      label="Empresa"
      :items="listCompanies"
      variant="outlined"
      density="comfortable"
      hide-details="auto"
      color="primary20"
    >
      <template #label>
        <span>Empresa <span class="text-red-500">*</span></span>
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-model="healthUnitValue"
      data-testid="import-form-unidade-select"
      :items="listHealthUnitsByCompany"
      variant="outlined"
      density="comfortable"
      hide-details="auto"
      color="primary20"
      :disabled="!modelValue.company || isLoadingUnits"
      :loading="isLoadingUnits"
    >
      <template #label>
        <span>Unidade <span class="text-red-500">*</span></span>
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-model="scheduleValues"
      data-testid="import-form-escala-select"
      :items="scheduleItems"
      variant="outlined"
      density="comfortable"
      hide-details="auto"
      color="primary20"
      multiple
      chips
      closable-chips
      :disabled="!modelValue.healthUnit || isLoadingSchedules || conflictsLoadFailed"
      :loading="isLoadingSchedules"
    >
      <template #label>
        <span>Escala(s) <span class="text-red-500">*</span></span>
      </template>
      <template #item="{ props: itemProps }">
        <v-list-item v-bind="itemProps">
          <template #prepend="{ isActive }">
            <v-checkbox-btn :model-value="isActive" color="primary20" />
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>

    <div
      v-if="conflictsLoadFailed"
      class="flex items-center gap-2 text-sm"
      style="color: rgb(var(--v-theme-error))"
    >
      <span>Erro ao carregar escalas.</span>
      <button
        type="button"
        class="retry-link"
        data-testid="import-conflicts-retry"
        @click="retryConflictsCheck"
      >
        Tente novamente
      </button>
    </div>

    <v-divider class="my-1" />

    <p class="text-sm text-textPrimary text-left">
      As importações de avaliação de políticas são feitas através de um arquivo .csv, que deve ser
      anexo nessa página.
    </p>

    <p class="text-sm text-textPrimary text-left">
      O arquivo deve respeitar o layout de colunas do modelo disponível abaixo. Veja
      <a href="/docs/import-templates-guide.md" target="_blank" class="text-primary20 underline">
        guia de formato
      </a>
      para detalhes sobre colunas e valores válidos.
    </p>

    <outlined-button
      data-testid="import-form-download-modelo-button"
      text="Download do modelo"
      :prepend-icon="mdiDownload"
      title="Baixa um arquivo CSV com headers e exemplo de linha. Preencha, salve como CSV (ponto-e-vírgula) e faça upload."
      class="!border-primary30 !text-primary30 !font-bold self-start"
      @click="handleDownloadTemplate"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { mdiDownload } from '@mdi/js'
import { useToast } from 'vue-toastification'

import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useImportQualificationValidationsStore } from '@/pinia-store/useImportQualificationValidationsStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import type { ImportFormData, QualificationPolicyType } from './types'
import { DEFAULT_POLICY_TYPE } from './types'

const props = withDefaults(
  defineProps<{
    modelValue: ImportFormData
    valid: boolean
    policyType?: QualificationPolicyType
  }>(),
  { policyType: DEFAULT_POLICY_TYPE }
)

const emit = defineEmits<{
  'update:modelValue': [value: ImportFormData]
  'update:valid': [value: boolean]
}>()

const companyStore = useCompanyStore()
const healthUnitStore = useHealthUnitStore()
const importStore = useImportQualificationValidationsStore()
const scheduleStore = useScheduleStore()

const { listCompanies } = storeToRefs(companyStore)
const { listHealthUnitsByCompany } = storeToRefs(healthUnitStore)
const { listSchedules } = storeToRefs(scheduleStore)

const toast = useToast()
const isLoadingUnits = ref(false)
const isLoadingSchedules = ref(false)
const conflictsLoadFailed = ref(false)
const { scheduleConflicts } = storeToRefs(importStore)
const requestIds = { units: 0, schedules: 0 }

const scheduleItems = computed(() =>
  listSchedules.value.map((item) => {
    const conflict = scheduleConflicts.value[Number(item.value)]
    if (!conflict) return item
    return {
      ...item,
      title: `${item.title} (já possui política ativa e vigente)`,
      props: { disabled: true }
    }
  })
)

const updateForm = (partial: Partial<ImportFormData>) => {
  emit('update:modelValue', { ...props.modelValue, ...partial })
}

const isValid = computed(
  () =>
    props.modelValue.company !== null &&
    props.modelValue.healthUnit !== null &&
    props.modelValue.schedules.length > 0
)

watch(isValid, (val) => emit('update:valid', val), { immediate: true })

const companyValue = computed<string | null>({
  get: () => (props.modelValue.company !== null ? String(props.modelValue.company) : null),
  set: (val) => {
    const num = Number(val)
    const company = val !== null && val !== undefined && !isNaN(num) ? num : null
    updateForm({ company, healthUnit: null, schedules: [] })
    handleCompanyChange(company)
  }
})

const healthUnitValue = computed<string | null>({
  get: () => (props.modelValue.healthUnit !== null ? String(props.modelValue.healthUnit) : null),
  set: (val) => {
    const num = Number(val)
    const healthUnit = val !== null && val !== undefined && !isNaN(num) ? num : null
    updateForm({ healthUnit, schedules: [] })
    handleHealthUnitChange(healthUnit)
  }
})

const scheduleValues = computed<string[]>({
  get: () => props.modelValue.schedules.map(String),
  set: (val) => {
    updateForm({ schedules: val.map(Number) })
  }
})

const handleCompanyChange = async (companyId: number | null) => {
  const requestId = ++requestIds.units
  scheduleConflicts.value = {}
  scheduleStore.setSchedulesByHealthUnit(null)
  if (!companyId) {
    healthUnitStore.setHealthUnitsByCompany(null)
    return
  }
  isLoadingUnits.value = true
  try {
    await healthUnitStore.setHealthUnitsByCompany(companyId)
  } catch {
    toast.error('Erro ao carregar unidades de saúde.')
  } finally {
    if (requestId === requestIds.units) isLoadingUnits.value = false
  }
}

const handleHealthUnitChange = async (healthUnitId: number | null) => {
  const requestId = ++requestIds.schedules
  scheduleConflicts.value = {}
  if (!healthUnitId) {
    scheduleStore.setSchedulesByHealthUnit(null)
    return
  }
  isLoadingSchedules.value = true
  conflictsLoadFailed.value = false
  try {
    await scheduleStore.setSchedulesByHealthUnit(healthUnitId)
    if (requestId === requestIds.schedules && props.modelValue.company) {
      await fetchConflictsAndFilter()
    }
  } catch {
    toast.error('Erro ao carregar escalas.')
  } finally {
    if (requestId === requestIds.schedules) isLoadingSchedules.value = false
  }
}

const fetchConflictsAndFilter = async () => {
  if (!props.modelValue.company) return
  const allIds = listSchedules.value.map((s) => Number(s.value))
  try {
    await importStore.fetchScheduleConflicts(props.modelValue.company, allIds, props.policyType)
    conflictsLoadFailed.value = false
    const conflictingIds = new Set(Object.keys(scheduleConflicts.value).map(Number))
    const filtered = props.modelValue.schedules.filter((id) => !conflictingIds.has(id))
    if (filtered.length !== props.modelValue.schedules.length) {
      updateForm({ schedules: filtered })
    }
  } catch {
    conflictsLoadFailed.value = true
    updateForm({ schedules: [] })
  }
}

const retryConflictsCheck = async () => {
  isLoadingSchedules.value = true
  try {
    await fetchConflictsAndFilter()
  } finally {
    isLoadingSchedules.value = false
  }
}

const templatePath = computed(() =>
  props.policyType === 'DOCUMENTARY_QUALIFICATION'
    ? '/qualificacao-documental/importacao_documental_modelo.csv'
    : '/qualificacao-repasse/importacao_repasse_modelo.csv'
)

const downloadFileName = computed(() =>
  props.policyType === 'DOCUMENTARY_QUALIFICATION'
    ? 'importacao_documental_modelo.csv'
    : 'importacao_repasse_modelo.csv'
)

const handleDownloadTemplate = async () => {
  try {
    const response = await fetch(templatePath.value)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', downloadFileName.value)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch {
    toast.error('Erro ao baixar modelo de importação.')
  }
}

onMounted(() => {
  if (!listCompanies.value.length) {
    companyStore.getMyCompanies()
  }
})
</script>

<style scoped>
.form-step :deep(.v-field--variant-outlined) {
  border-radius: 8px;
}
.form-step :deep(.v-label) {
  color: theme('colors.gray.600');
  opacity: 1;
}
.form-step :deep(.v-list-item--disabled) {
  opacity: 0.5;
}
.retry-link {
  all: unset;
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.75rem;
}
.retry-link:hover {
  filter: brightness(0.8);
}
.retry-link:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
  border-radius: 2px;
}
</style>
